/**
 * CRS Aligner module per RFC-112.
 * 
 * Pure math kernel for aligning any source-CRS layer into the project frame.
 * Handles ECEF → Mercator, proj4 transformations, LOCAL+anchor cases,
 * and scale modes for visual vs engineering accuracy.
 */

import proj4 from 'proj4';
import { getCrsDef, normalizeCrsCode, UnknownCrsError } from './crsCatalog';

export type ScaleMode = 'visual' | 'engineering';

export type SourceBounds =
  | { kind: 'region'; region: readonly [number, number, number, number, number, number] }      // 3D Tiles region (rad)
  | { kind: 'boxWithTransform'; box: readonly number[]; transform?: readonly number[] }         // 3D Tiles box + optional transform (column-major 16)
  | { kind: 'localBox'; box: readonly number[]; anchor: AnchorLocation }                        // LOCAL with operator anchor
  | { kind: 'bbox4326'; bbox: readonly [number, number, number, number] };                      // raster/vector geographic bbox

export interface AnchorLocation {
  lat: number;        // degrees
  lon: number;        // degrees
  height?: number;    // meters (default 0)
  heading?: number;   // degrees, default 0 — rotation around local Up
}

export interface AlignmentInput {
  sourceCrs: string;            // any input the catalog can normalize
  frameCrs: string;             // 'EPSG:3857' (legacy) OR a converter displayCrs
  bounds: SourceBounds;
  scaleMode: ScaleMode;         // 'visual' default at caller-site
  /** When set, all ECEF tilesets in a multi-layer scene share one ENU origin. */
  sharedAnchor?: AnchorLocation;
  /**
   * INIT-2026-Q3-SCENE-HIERARCHY V-CRS-A — proj4 definition string for a
   * non-mercator projected `frameCrs` (the converter-supplied `displayCrs`).
   * When present, `align()` reprojects ECEF → `frameCrs` per block at unit
   * scale (no Mercator inflation) with MSL pass-through, instead of the
   * single-tangent Web-Mercator path.
   */
  frameCrsProj4?: string;
}

export interface Alignment {
  matrix: number[];                            // length 16, column-major
  anchorFrame: [number, number, number];       // anchor position in frame CRS units
  anchorLatLon: [number, number];              // for camera + UI
  mercatorScaleAtAnchor: number;               // 1/cos(lat) when frame is EPSG:3857; else 1
  appliedScale: number;                        // mercatorScaleAtAnchor (visual) or 1 (engineering)
  scaleMode: ScaleMode;
  correctionLabel: string;                     // e.g. "EPSG:4978 → EPSG:3857"
  correctionDetail: string;                    // human-readable summary including anchor and scale
  rejected?: { reason: string; code: 'UNKNOWN_CRS' | 'MISSING_BOUNDS' | 'PROJ4_FAILED' | 'UNSUPPORTED_FRAME' };
}

// WGS84 ellipsoid constants
const WGS84_A = 6378137.0; // semi-major axis in meters
const WGS84_F = 1.0 / 298.257223563; // flattening
const WGS84_E2 = 2 * WGS84_F - WGS84_F * WGS84_F; // first eccentricity squared

// Mercator latitude clamp (±85.05112878°)
const MERCATOR_MAX_LAT = 85.05112878 * Math.PI / 180;

// Memoize proj4 definitions to avoid re-registration
const registeredProj4Defs = new Set<string>();

/**
 * Register a CRS definition with proj4 if not already done.
 */
async function ensureProj4Def(code: string): Promise<void> {
  const normalizedCode = normalizeCrsCode(code);
  
  if (registeredProj4Defs.has(normalizedCode)) {
    return;
  }
  
  // LOCAL is special - no proj4 registration needed
  if (normalizedCode === 'LOCAL') {
    registeredProj4Defs.add(normalizedCode);
    return;
  }
  
  const crsDef = await getCrsDef(normalizedCode);
  if (crsDef.proj4) {
    proj4.defs(normalizedCode, crsDef.proj4);
    registeredProj4Defs.add(normalizedCode);
  }
}

// EPSG:4978 (WGS84 geocentric / ECEF) is NOT predefined by proj4. We register
// it once so `proj4('EPSG:4978', <displayCrs>)` can reproject block centroids.
const GEOCENTRIC_WGS84_PROJ4 = '+proj=geocent +datum=WGS84 +units=m +no_defs';
let ecefProj4Registered = false;

/**
 * V-CRS-A — register the ECEF source def and (when supplied) the projected
 * `displayCrs` def with proj4. EPSG:4326 / WGS84 ship predefined with proj4.
 */
function ensureDisplayFrameDefs(frameCrs: string, frameCrsProj4?: string): void {
  if (!ecefProj4Registered) {
    if (!proj4.defs('EPSG:4978')) {
      proj4.defs('EPSG:4978', GEOCENTRIC_WGS84_PROJ4);
    }
    ecefProj4Registered = true;
  }
  const normalizedFrame = normalizeCrsCode(frameCrs);
  if (frameCrsProj4 && !registeredProj4Defs.has(normalizedFrame)) {
    proj4.defs(normalizedFrame, frameCrsProj4);
    registeredProj4Defs.add(normalizedFrame);
  }
}

/**
 * Derive the ECEF (EPSG:4978) centroid for a block from its bounds.
 * Region values are radians (3D Tiles); box/transform are ECEF metres.
 */
/**
 * 3D Tiles oriented-box centre in world ECEF metres.
 * Policy 118: when both box and transform are present, the anchor is the
 * geometry centre (box centre transformed), not the frame origin alone.
 * Our pipeline uses translation-only root transforms (identity rotation).
 */
export function deriveEcefWorldCenterFromBounds(bounds: SourceBounds): [number, number, number] | null {
  if (bounds.kind === 'region') {
    const [west, south, east, north, minHeight, maxHeight] = bounds.region;
    const lonRad = (west + east) / 2;
    const latRad = (south + north) / 2;
    const height = (minHeight + maxHeight) / 2;
    return wgs84GeodeticToEcef(lonRad, latRad, height);
  }
  if (bounds.kind === 'boxWithTransform') {
    if (bounds.box.length >= 3) {
      const cx = bounds.box[0];
      const cy = bounds.box[1];
      const cz = bounds.box[2];
      if (bounds.transform && bounds.transform.length === 16) {
        const t = bounds.transform;
        return [cx + t[12], cy + t[13], cz + t[14]];
      }
      return [cx, cy, cz];
    }
  }
  return null;
}

function deriveEcefAnchorFromBounds(bounds: SourceBounds): [number, number, number] | null {
  return deriveEcefWorldCenterFromBounds(bounds);
}

/**
 * INIT-2026-Q3-SCENE-HIERARCHY V-CRS-A — reproject one ECEF block into the
 * dataset's projected `displayCrs`.
 *
 * Per policy 118 / render-frame spec: project the tileset world-centre ECEF →
 * displayCrs (east, north) via proj4, place the group at `(east, north, h)`.
 * An ENU rotation at the anchor orients ECEF geometry into the local
 * East-North-Up frame so that buildings are upright and nearby blocks share
 * the same vertical datum. Scale is exactly 1; vertical is MSL pass-through.
 */
async function handleEcefToDisplayCrs(
  anchorEcef: [number, number, number],
  frameCrs: string,
  frameCrsProj4: string | undefined,
  scaleMode: ScaleMode,
): Promise<Alignment> {
  ensureDisplayFrameDefs(frameCrs, frameCrsProj4);
  // Fall back to the catalog/IDB/HTTP def when no inline proj4 string was given.
  if (!frameCrsProj4) {
    await ensureProj4Def(frameCrs);
  }

  // ECEF → geodetic (lon°, lat°, ellipsoidal height m).
  const geodetic = proj4('EPSG:4978', 'EPSG:4326', [anchorEcef[0], anchorEcef[1], anchorEcef[2]]);
  const anchorLon = geodetic[0];
  const anchorLat = geodetic[1];
  const anchorHeight = Number.isFinite(geodetic[2]) ? (geodetic[2] as number) : 0;

  // geodetic → displayCrs (east, north).
  const projected = proj4('EPSG:4326', frameCrs, [anchorLon, anchorLat]);
  const east = projected[0];
  const north = projected[1];

  // MSL pass-through: keep the model's orthometric height, no scale on Z.
  const anchorFrame: [number, number, number] = [east, north, anchorHeight];

  // ENU rotation: rotate ECEF axes to local East-North-Up at the anchor.
  const latRad = anchorLat * Math.PI / 180;
  const lonRad = anchorLon * Math.PI / 180;
  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const sinLon = Math.sin(lonRad);
  const cosLon = Math.cos(lonRad);

  // Column-major 3×3 ENU rotation (ECEF → East, North, Up)
  const enuRot3x3 = [
    -sinLon,           -sinLat * cosLon,  cosLat * cosLon,   // col 0
    cosLon,            -sinLat * sinLon,  cosLat * sinLon,   // col 1
    0,                  cosLat,           sinLat,            // col 2
  ];

  // Combined: M = T(anchorFrame) * R_enu * T(-ecefAnchor)
  // For point P: P' = R_enu*(P - ecefAnchor) + anchorFrame
  // Translation = anchorFrame - R_enu * ecefAnchor
  const rx = enuRot3x3[0]*anchorEcef[0] + enuRot3x3[3]*anchorEcef[1] + enuRot3x3[6]*anchorEcef[2];
  const ry = enuRot3x3[1]*anchorEcef[0] + enuRot3x3[4]*anchorEcef[1] + enuRot3x3[7]*anchorEcef[2];
  const rz = enuRot3x3[2]*anchorEcef[0] + enuRot3x3[5]*anchorEcef[1] + enuRot3x3[8]*anchorEcef[2];

  const matrix = createMatrix4(
    [anchorFrame[0] - rx, anchorFrame[1] - ry, anchorFrame[2] - rz],
    enuRot3x3,
  );

  return {
    matrix,
    anchorFrame,
    anchorLatLon: [anchorLat, anchorLon],
    mercatorScaleAtAnchor: 1,
    appliedScale: 1,
    scaleMode,
    correctionLabel: `EPSG:4978 → ${frameCrs}`,
    correctionDetail:
      `ECEF → ${frameCrs} at anchor ${anchorLat.toFixed(4)}°, ${anchorLon.toFixed(4)}° ` +
      `(ENU rotation, scale 1, MSL pass-through)`,
  };
}

/**
 * Convert WGS84 geodetic coordinates to ECEF.
 */
function wgs84GeodeticToEcef(lonRad: number, latRad: number, height: number): [number, number, number] {
  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const sinLon = Math.sin(lonRad);
  const cosLon = Math.cos(lonRad);
  
  const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);
  
  const x = (N + height) * cosLat * cosLon;
  const y = (N + height) * cosLat * sinLon;
  const z = (N * (1 - WGS84_E2) + height) * sinLat;
  
  return [x, y, z];
}

/**
 * Build ENU rotation matrix from ECEF at given geodetic coordinates.
 * Returns 3x3 matrix as flat array in column-major order.
 */
function buildEnuRotation(lonRad: number, latRad: number): number[] {
  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const sinLon = Math.sin(lonRad);
  const cosLon = Math.cos(lonRad);
  
  // ENU to ECEF rotation matrix (column-major)
  return [
    -sinLon,           // East x
    -sinLat * cosLon,  // North x
    cosLat * cosLon,   // Up x
    cosLon,            // East y
    -sinLat * sinLon,  // North y
    cosLat * sinLon,   // Up y
    0,                 // East z
    cosLat,            // North z
    sinLat             // Up z
  ];
}

/**
 * Create a 4x4 transformation matrix in column-major order.
 * Column-major layout (element access by index):
 * [ 0  4  8 12]   [m00 m01 m02 tx ]
 * [ 1  5  9 13] = [m10 m11 m12 ty ]
 * [ 2  6 10 14]   [m20 m21 m22 tz ]
 * [ 3  7 11 15]   [ 0   0   0   1 ]
 */
function createMatrix4(
  translation: [number, number, number],
  rotation3x3?: number[], // column-major 3x3, optional
  scale?: number
): number[] {
  const matrix = new Array(16);
  
  // Start with identity
  matrix[0] = 1; matrix[4] = 0; matrix[8] = 0; matrix[12] = translation[0];
  matrix[1] = 0; matrix[5] = 1; matrix[9] = 0; matrix[13] = translation[1];
  matrix[2] = 0; matrix[6] = 0; matrix[10] = 1; matrix[14] = translation[2];
  matrix[3] = 0; matrix[7] = 0; matrix[11] = 0; matrix[15] = 1;
  
  // Apply rotation if provided
  if (rotation3x3) {
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        matrix[i + j * 4] = rotation3x3[i + j * 3];
      }
    }
  }
  
  // Apply scale if provided
  if (scale !== undefined && scale !== 1) {
    for (let col = 0; col < 3; col++) {
      matrix[0 + col * 4] *= scale;
      matrix[1 + col * 4] *= scale;
      matrix[2 + col * 4] *= scale;
    }
  }
  
  return matrix;
}

/**
 * Multiply 4x4 matrices A * B, both in column-major order.
 */
function multiplyMatrix4(a: number[], b: number[]): number[] {
  const result = new Array(16);
  
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      result[row + col * 4] = 
        a[row + 0 * 4] * b[0 + col * 4] +
        a[row + 1 * 4] * b[1 + col * 4] +
        a[row + 2 * 4] * b[2 + col * 4] +
        a[row + 3 * 4] * b[3 + col * 4];
    }
  }
  
  return result;
}

/**
 * Clamp latitude to Mercator bounds and convert lat/lon to Web Mercator.
 */
function latLonToWebMercator(latDeg: number, lonDeg: number): [number, number] {
  const latRad = Math.max(-MERCATOR_MAX_LAT, Math.min(MERCATOR_MAX_LAT, latDeg * Math.PI / 180));
  const lonRad = lonDeg * Math.PI / 180;
  
  const x = WGS84_A * lonRad;
  const y = WGS84_A * Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  
  return [x, y];
}

/**
 * Handle ECEF (EPSG:4978) region tileset alignment.
 */
async function handleEcefRegion(
  region: readonly [number, number, number, number, number, number],
  frameCrs: string,
  scaleMode: ScaleMode,
  sharedAnchor?: AnchorLocation,
): Promise<Alignment> {
  const [west, south, east, north, minHeight, maxHeight] = region;
  
  // Extract anchor from region center (convert radians to degrees), or reuse
  // the project-wide anchor so every PLATEAU layer shares one ENU frame.
  const anchorLat = sharedAnchor?.lat ?? ((south + north) / 2) * 180 / Math.PI;
  const anchorLon = sharedAnchor?.lon ?? ((west + east) / 2) * 180 / Math.PI;
  const anchorHeight = sharedAnchor?.height ?? (minHeight + maxHeight) / 2;
  
  // Convert to ECEF
  const anchorEcef = wgs84GeodeticToEcef(
    anchorLon * Math.PI / 180,
    anchorLat * Math.PI / 180, 
    anchorHeight
  );
  
  // Build ENU rotation at anchor
  const enuRotation = buildEnuRotation(
    anchorLon * Math.PI / 180,
    anchorLat * Math.PI / 180
  );
  
  // Convert anchor to frame CRS
  let anchorFrame: [number, number, number];
  if (frameCrs === 'EPSG:3857') {
    const [mx, my] = latLonToWebMercator(anchorLat, anchorLon);
    anchorFrame = [mx, my, anchorHeight];
  } else {
    throw new Error(`Unsupported frame CRS: ${frameCrs}`);
  }
  
  // Calculate scale factors
  const mercatorScaleAtAnchor = frameCrs === 'EPSG:3857' 
    ? 1 / Math.cos(anchorLat * Math.PI / 180)
    : 1;
  const appliedScale = scaleMode === 'visual' ? mercatorScaleAtAnchor : 1;
  
  // Compose transformation matrix: T(mercator) * S(scale) * R(enu) * T(-ecef)
  // Start with negative ECEF translation
  let matrix = createMatrix4([-anchorEcef[0], -anchorEcef[1], -anchorEcef[2]]);
  
  // Apply ENU rotation
  const rotationMatrix = createMatrix4([0, 0, 0], enuRotation);
  matrix = multiplyMatrix4(rotationMatrix, matrix);
  
  // Apply scaling
  if (appliedScale !== 1) {
    const scalingMatrix = createMatrix4([0, 0, 0], undefined, appliedScale);
    matrix = multiplyMatrix4(scalingMatrix, matrix);
  }
  
  // Apply final translation to Mercator coordinates
  const finalTranslation = createMatrix4(anchorFrame);
  matrix = multiplyMatrix4(finalTranslation, matrix);
  
  return {
    matrix,
    anchorFrame,
    anchorLatLon: [anchorLat, anchorLon],
    mercatorScaleAtAnchor,
    appliedScale,
    scaleMode,
    correctionLabel: `EPSG:4978 → ${frameCrs}`,
    correctionDetail: `ECEF to ${frameCrs} with ENU rotation at anchor ${anchorLat.toFixed(4)}°, ${anchorLon.toFixed(4)}°, scale mode: ${scaleMode}`
  };
}

/**
 * Handle ECEF boxWithTransform case.
 */
async function handleEcefBoxWithTransform(
  box: readonly number[],
  transform: readonly number[] | undefined,
  frameCrs: string,
  scaleMode: ScaleMode,
  sharedAnchor?: AnchorLocation,
): Promise<Alignment> {
  let anchorEcef: [number, number, number];
  
  if (transform && transform.length === 16) {
    // Extract translation from the transform matrix (column-major: elements 12, 13, 14)
    anchorEcef = [transform[12], transform[13], transform[14]];
  } else {
    // No transform — the 3D Tiles box is ECEF-absolute (e.g. a parent
    // tileset-of-tilesets whose children carry their own transforms).
    // 3D Tiles box layout is [cx, cy, cz, halfX(3), halfY(3), halfZ(3)] — the
    // CENTER is box[0..2]. (Do NOT average with box[3..5]; those are the X
    // half-axis vector, not a max corner. Averaging halved the ECEF magnitude
    // and put the anchor ~3185 km underground, hiding the whole layer.)
    if (box.length < 3) {
      throw new Error('Box must have at least 3 elements for a 3D center');
    }
    anchorEcef = [box[0], box[1], box[2]];
  }
  
  // Convert ECEF to geodetic for anchor (unless a project-wide anchor is supplied).
  let anchorLat: number;
  let anchorLon: number;
  let anchorHeight: number;
  let anchorEcefForTransform: [number, number, number];
  if (sharedAnchor) {
    anchorLat = sharedAnchor.lat;
    anchorLon = sharedAnchor.lon;
    anchorHeight = sharedAnchor.height ?? 0;
    anchorEcefForTransform = wgs84GeodeticToEcef(
      anchorLon * Math.PI / 180,
      anchorLat * Math.PI / 180,
      anchorHeight,
    );
  } else {
    const r = Math.sqrt(anchorEcef[0] * anchorEcef[0] + anchorEcef[1] * anchorEcef[1]);
    anchorLon = Math.atan2(anchorEcef[1], anchorEcef[0]) * 180 / Math.PI;
    anchorLat = Math.atan2(anchorEcef[2], r) * 180 / Math.PI; // simplified
    anchorHeight = Math.sqrt(anchorEcef[0] * anchorEcef[0] + anchorEcef[1] * anchorEcef[1] + anchorEcef[2] * anchorEcef[2]) - WGS84_A;
    anchorEcefForTransform = anchorEcef;
  }
  
  // Build ENU rotation at anchor
  const enuRotation = buildEnuRotation(
    anchorLon * Math.PI / 180,
    anchorLat * Math.PI / 180
  );
  
  // Convert anchor to frame CRS
  let anchorFrame: [number, number, number];
  if (frameCrs === 'EPSG:3857') {
    const [mx, my] = latLonToWebMercator(anchorLat, anchorLon);
    anchorFrame = [mx, my, anchorHeight];
  } else {
    throw new Error(`Unsupported frame CRS: ${frameCrs}`);
  }
  
  // Calculate scale factors
  const mercatorScaleAtAnchor = frameCrs === 'EPSG:3857' 
    ? 1 / Math.cos(anchorLat * Math.PI / 180)
    : 1;
  const appliedScale = scaleMode === 'visual' ? mercatorScaleAtAnchor : 1;
  
  // Compose transformation matrix similar to region case
  // Start with negative ECEF translation
  let matrix = createMatrix4([-anchorEcefForTransform[0], -anchorEcefForTransform[1], -anchorEcefForTransform[2]]);
  
  // Apply ENU rotation
  const rotationMatrix = createMatrix4([0, 0, 0], enuRotation);
  matrix = multiplyMatrix4(rotationMatrix, matrix);
  
  // Apply scaling
  if (appliedScale !== 1) {
    const scalingMatrix = createMatrix4([0, 0, 0], undefined, appliedScale);
    matrix = multiplyMatrix4(scalingMatrix, matrix);
  }
  
  // Apply final translation to Mercator coordinates
  const finalTranslation = createMatrix4(anchorFrame);
  matrix = multiplyMatrix4(finalTranslation, matrix);
  
  return {
    matrix,
    anchorFrame,
    anchorLatLon: [anchorLat, anchorLon],
    mercatorScaleAtAnchor,
    appliedScale,
    scaleMode,
    correctionLabel: `EPSG:4978 → ${frameCrs}`,
    correctionDetail: `ECEF box to ${frameCrs} with ENU rotation at anchor ${anchorLat.toFixed(4)}°, ${anchorLon.toFixed(4)}°, scale mode: ${scaleMode}`
  };
}

/**
 * Handle bbox4326 (EPSG:4326 lat/lon bbox) for raster/vector.
 */
async function handleBbox4326(
  bbox: readonly [number, number, number, number],
  frameCrs: string,
  scaleMode: ScaleMode
): Promise<Alignment> {
  const [west, south, east, north] = bbox;
  const anchorLat = (south + north) / 2;
  const anchorLon = (west + east) / 2;
  
  // Ensure proj4 definitions are registered
  await ensureProj4Def('EPSG:4326');
  await ensureProj4Def(frameCrs);
  
  let anchorFrame: [number, number, number];
  if (frameCrs === 'EPSG:3857') {
    const [mx, my] = latLonToWebMercator(anchorLat, anchorLon);
    anchorFrame = [mx, my, 0];
  } else if (frameCrs === 'EPSG:4326') {
    // Identity case
    anchorFrame = [anchorLon, anchorLat, 0];
  } else {
    // Use proj4 for transformation
    const transformed = proj4('EPSG:4326', frameCrs, [anchorLon, anchorLat]);
    anchorFrame = [transformed[0], transformed[1], 0];
  }
  
  // Calculate scale factors
  const mercatorScaleAtAnchor = frameCrs === 'EPSG:3857' 
    ? 1 / Math.cos(anchorLat * Math.PI / 180)
    : 1;
  const appliedScale = scaleMode === 'visual' ? mercatorScaleAtAnchor : 1;
  
  // For raster/vector, typically use identity transformation with translation to anchor
  const matrix = createMatrix4(anchorFrame, undefined, appliedScale);
  
  return {
    matrix,
    anchorFrame,
    anchorLatLon: [anchorLat, anchorLon],
    mercatorScaleAtAnchor,
    appliedScale,
    scaleMode,
    correctionLabel: `EPSG:4326 → ${frameCrs}`,
    correctionDetail: `Geographic to ${frameCrs} at center ${anchorLat.toFixed(4)}°, ${anchorLon.toFixed(4)}°, scale mode: ${scaleMode}`
  };
}

/**
 * Handle localBox + anchor (CAD models with geographic reference).
 */
async function handleLocalBoxWithAnchor(
  _box: readonly number[],
  anchor: AnchorLocation,
  frameCrs: string,
  scaleMode: ScaleMode
): Promise<Alignment> {
  const { lat, lon, height = 0, heading = 0 } = anchor;
  
  // Convert anchor to ECEF
  const anchorEcef = wgs84GeodeticToEcef(
    lon * Math.PI / 180,
    lat * Math.PI / 180,
    height
  );
  
  // Build heading rotation (around local Up/Z axis) if provided
  const headingRad = heading * Math.PI / 180;
  const headingRotation = headingRad !== 0 ? [
    Math.cos(headingRad), -Math.sin(headingRad), 0,  // X column
    Math.sin(headingRad),  Math.cos(headingRad), 0,  // Y column  
    0,                     0,                    1   // Z column
  ] : undefined;
  
  // Build ENU rotation at anchor
  const enuRotation = buildEnuRotation(
    lon * Math.PI / 180,
    lat * Math.PI / 180
  );
  
  // Convert anchor to frame CRS
  let anchorFrame: [number, number, number];
  if (frameCrs === 'EPSG:3857') {
    const [mx, my] = latLonToWebMercator(lat, lon);
    anchorFrame = [mx, my, height];
  } else {
    throw new Error(`Unsupported frame CRS: ${frameCrs}`);
  }
  
  // Calculate scale factors
  const mercatorScaleAtAnchor = frameCrs === 'EPSG:3857' 
    ? 1 / Math.cos(lat * Math.PI / 180)
    : 1;
  const appliedScale = scaleMode === 'visual' ? mercatorScaleAtAnchor : 1;
  
  // Compose transformation: T(mercator) * S(scale) * R(enu) * T(anchor_ecef) * R(heading)
  let matrix = createMatrix4([0, 0, 0]); // Start with identity
  
  if (headingRotation) {
    const headingMatrix = createMatrix4([0, 0, 0], headingRotation);
    matrix = multiplyMatrix4(matrix, headingMatrix);
  }
  
  const anchorTranslation = createMatrix4(anchorEcef);
  matrix = multiplyMatrix4(anchorTranslation, matrix);
  
  const enuMatrix = createMatrix4([0, 0, 0], enuRotation);
  matrix = multiplyMatrix4(enuMatrix, matrix);
  
  const scalingMatrix = createMatrix4([0, 0, 0], undefined, appliedScale);
  matrix = multiplyMatrix4(scalingMatrix, matrix);
  
  const finalTranslation = createMatrix4(anchorFrame);
  matrix = multiplyMatrix4(finalTranslation, matrix);
  
  return {
    matrix,
    anchorFrame,
    anchorLatLon: [lat, lon],
    mercatorScaleAtAnchor,
    appliedScale,
    scaleMode,
    correctionLabel: `LOCAL → ${frameCrs}`,
    correctionDetail: `Local to ${frameCrs} via anchor ${lat.toFixed(4)}°, ${lon.toFixed(4)}°${heading !== 0 ? ` with ${heading.toFixed(1)}° heading` : ''}, scale mode: ${scaleMode}`
  };
}

/**
 * Handle projected source CRS to frame CRS via proj4.
 */
async function handleProjectedToFrame(
  sourceCrs: string,
  frameCrs: string,
  bounds: SourceBounds,
  scaleMode: ScaleMode
): Promise<Alignment> {
  await ensureProj4Def(sourceCrs);
  await ensureProj4Def(frameCrs);
  
  // For projected transformations, we need some bounds to establish an anchor
  let anchorLat: number, anchorLon: number;
  let anchorFrameFromGeographicBbox = false;
  
  if (bounds.kind === 'bbox4326') {
    const [west, south, east, north] = bounds.bbox;
    anchorLat = (south + north) / 2;
    anchorLon = (west + east) / 2;
    anchorFrameFromGeographicBbox = true;
  } else {
    // Use default anchor at origin for other bound types
    anchorLat = 0;
    anchorLon = 0;
  }
  
  // Transform anchor via proj4
  let anchorFrame: [number, number, number];
  try {
    if (anchorFrameFromGeographicBbox) {
      if (frameCrs === 'EPSG:3857') {
        const [mx, my] = latLonToWebMercator(anchorLat, anchorLon);
        anchorFrame = [mx, my, 0];
      } else if (frameCrs === 'EPSG:4326') {
        anchorFrame = [anchorLon, anchorLat, 0];
      } else {
        const transformed = proj4('EPSG:4326', frameCrs, [anchorLon, anchorLat]);
        anchorFrame = [transformed[0], transformed[1], 0];
      }
    } else if (sourceCrs === frameCrs) {
      // Identity case
      if (frameCrs === 'EPSG:3857') {
        const [mx, my] = latLonToWebMercator(anchorLat, anchorLon);
        anchorFrame = [mx, my, 0];
      } else {
        anchorFrame = [anchorLon, anchorLat, 0]; // Assume lon/lat order for other CRS
      }
    } else {
      // Need to determine source coordinates - this is simplified
      // In practice, this would need bounds in source CRS
      const sourceCoords = [anchorLon, anchorLat]; // Assume lat/lon for now
      const transformed = proj4(sourceCrs, frameCrs, sourceCoords);
      anchorFrame = [transformed[0], transformed[1], 0];
    }
  } catch (error) {
    throw new Error(`Proj4 transformation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
  
  // Calculate scale factors
  const mercatorScaleAtAnchor = frameCrs === 'EPSG:3857' 
    ? 1 / Math.cos(anchorLat * Math.PI / 180)
    : 1;
  const appliedScale = scaleMode === 'visual' ? mercatorScaleAtAnchor : 1;
  
  // For projected transformations, typically use identity matrix with anchor translation
  const matrix = createMatrix4(anchorFrame, undefined, appliedScale);
  
  return {
    matrix,
    anchorFrame,
    anchorLatLon: [anchorLat, anchorLon],
    mercatorScaleAtAnchor,
    appliedScale,
    scaleMode,
    correctionLabel: `${sourceCrs} → ${frameCrs}`,
    correctionDetail: `Projected transformation from ${sourceCrs} to ${frameCrs} at anchor ${anchorLat.toFixed(4)}°, ${anchorLon.toFixed(4)}°${anchorFrameFromGeographicBbox ? ' (geographic bbox anchor)' : ''}, scale mode: ${scaleMode}`
  };
}

/**
 * Main alignment function per RFC-112 contract.
 */
export async function align(input: AlignmentInput): Promise<Alignment> {
  const { sourceCrs, frameCrs, bounds, scaleMode, sharedAnchor, frameCrsProj4 } = input;
  
  try {
    // Normalize CRS codes
    const normalizedSourceCrs = normalizeCrsCode(sourceCrs);
    const normalizedFrameCrs = normalizeCrsCode(frameCrs);
    
    // INIT-2026-Q3-SCENE-HIERARCHY V-CRS-A — when the frame is a projected
    // displayCrs (anything other than legacy Web Mercator), reproject ECEF
    // blocks into it per-block at unit scale instead of rejecting. Non-ECEF
    // sources keep the legacy Phase-1 behaviour below.
    if (normalizedFrameCrs !== 'EPSG:3857') {
      if (
        normalizedSourceCrs === 'EPSG:4978'
        && (bounds.kind === 'region' || bounds.kind === 'boxWithTransform')
      ) {
        const anchorEcef = deriveEcefAnchorFromBounds(bounds);
        if (anchorEcef) {
          return await handleEcefToDisplayCrs(anchorEcef, normalizedFrameCrs, frameCrsProj4, scaleMode);
        }
      }
      return {
        matrix: createMatrix4([0, 0, 0]), // Identity matrix
        anchorFrame: [0, 0, 0],
        anchorLatLon: [0, 0],
        mercatorScaleAtAnchor: 1,
        appliedScale: 1,
        scaleMode,
        correctionLabel: `${normalizedSourceCrs} → ${normalizedFrameCrs}`,
        correctionDetail: `Frame CRS ${normalizedFrameCrs} not supported in Phase 1`,
        rejected: {
          reason: 'Only EPSG:3857 frame is supported in Phase 1',
          code: 'UNSUPPORTED_FRAME'
        }
      };
    }
    
    // Identity case
    if (normalizedSourceCrs === normalizedFrameCrs) {
      let anchorFrame: [number, number, number] = [0, 0, 0];
      let anchorLatLon: [number, number] = [0, 0];
      
      if (bounds.kind === 'bbox4326') {
        const [west, south, east, north] = bounds.bbox;
        const anchorLat = (south + north) / 2;
        const anchorLon = (west + east) / 2;
        const [mx, my] = latLonToWebMercator(anchorLat, anchorLon);
        anchorFrame = [mx, my, 0];
        anchorLatLon = [anchorLat, anchorLon];
      }
      
      const mercatorScaleAtAnchor = 1 / Math.cos(anchorLatLon[0] * Math.PI / 180);
      const appliedScale = scaleMode === 'visual' ? mercatorScaleAtAnchor : 1;
      
      return {
        matrix: createMatrix4(anchorFrame, undefined, appliedScale),
        anchorFrame,
        anchorLatLon,
        mercatorScaleAtAnchor,
        appliedScale,
        scaleMode,
        correctionLabel: `${normalizedSourceCrs} → ${normalizedFrameCrs}`,
        correctionDetail: `Identity transformation, scale mode: ${scaleMode}`
      };
    }
    
    // Handle different source CRS cases
    if (normalizedSourceCrs === 'EPSG:4978') {
      // ECEF cases
      if (bounds.kind === 'region') {
        return await handleEcefRegion(bounds.region, normalizedFrameCrs, scaleMode, sharedAnchor);
      } else if (bounds.kind === 'boxWithTransform') {
        return await handleEcefBoxWithTransform(bounds.box, bounds.transform, normalizedFrameCrs, scaleMode, sharedAnchor);
      } else {
        return {
          matrix: createMatrix4([0, 0, 0]),
          anchorFrame: [0, 0, 0],
          anchorLatLon: [0, 0],
          mercatorScaleAtAnchor: 1,
          appliedScale: 1,
          scaleMode,
          correctionLabel: `${normalizedSourceCrs} → ${normalizedFrameCrs}`,
          correctionDetail: `ECEF source requires region or boxWithTransform bounds`,
          rejected: {
            reason: 'ECEF source requires region or boxWithTransform bounds',
            code: 'MISSING_BOUNDS'
          }
        };
      }
    } else if (normalizedSourceCrs === 'LOCAL') {
      // Local coordinate system
      if (bounds.kind === 'localBox') {
        return await handleLocalBoxWithAnchor(bounds.box, bounds.anchor, normalizedFrameCrs, scaleMode);
      } else {
        return {
          matrix: createMatrix4([0, 0, 0]),
          anchorFrame: [0, 0, 0],
          anchorLatLon: [0, 0],
          mercatorScaleAtAnchor: 1,
          appliedScale: 1,
          scaleMode,
          correctionLabel: `${normalizedSourceCrs} → ${normalizedFrameCrs}`,
          correctionDetail: `LOCAL source requires localBox bounds with anchor`,
          rejected: {
            reason: 'LOCAL source requires localBox bounds with anchor',
            code: 'MISSING_BOUNDS'
          }
        };
      }
    } else if (normalizedSourceCrs === 'EPSG:4326') {
      // Geographic WGS84
      if (bounds.kind === 'bbox4326') {
        return await handleBbox4326(bounds.bbox, normalizedFrameCrs, scaleMode);
      } else {
        // Convert other bound types to bbox4326 if possible
        return await handleProjectedToFrame(normalizedSourceCrs, normalizedFrameCrs, bounds, scaleMode);
      }
    } else {
      // Other projected CRS - use proj4
      return await handleProjectedToFrame(normalizedSourceCrs, normalizedFrameCrs, bounds, scaleMode);
    }
    
  } catch (error) {
    if (error instanceof UnknownCrsError) {
      return {
        matrix: createMatrix4([0, 0, 0]),
        anchorFrame: [0, 0, 0], 
        anchorLatLon: [0, 0],
        mercatorScaleAtAnchor: 1,
        appliedScale: 1,
        scaleMode,
        correctionLabel: `${sourceCrs} → ${frameCrs}`,
        correctionDetail: `Unknown CRS: ${error.code}`,
        rejected: {
          reason: error.message,
          code: 'UNKNOWN_CRS'
        }
      };
    }
    
    return {
      matrix: createMatrix4([0, 0, 0]),
      anchorFrame: [0, 0, 0],
      anchorLatLon: [0, 0], 
      mercatorScaleAtAnchor: 1,
      appliedScale: 1,
      scaleMode,
      correctionLabel: `${sourceCrs} → ${frameCrs}`,
      correctionDetail: `Alignment failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      rejected: {
        reason: error instanceof Error ? error.message : 'Unknown error',
        code: 'PROJ4_FAILED'
      }
    };
  }
}